import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { mcpServersAPI, categoriesAPI } from '../services/api';
import { Card } from '../components/ui';
import WizardProgress from '../components/mcp/WizardProgress';
import MCPServerFormStep1 from '../components/mcp/MCPServerFormStep1';
import MCPServerFormStep2 from '../components/mcp/MCPServerFormStep2';
import MCPServerFormStep3 from '../components/mcp/MCPServerFormStep3';
import MCPServerFormStep4 from '../components/mcp/MCPServerFormStep4';
import MCPServerFormStep5 from '../components/mcp/MCPServerFormStep5';
import MCPServerFormStep6 from '../components/mcp/MCPServerFormStep6';
import MCPServerFormStep7 from '../components/mcp/MCPServerFormStep7';
import MCPServerFormStep8 from '../components/mcp/MCPServerFormStep8';
import { FaArrowLeft, FaArrowRight, FaRocket } from 'react-icons/fa';
import { contentTypes } from '../utils/designTokens';

const CreateMCPServer = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const totalSteps = 8;
  const mcpColor = contentTypes.mcp_server;

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    title: '',
    description: '',
    iconUrl: '',
    categoryId: '',
    tags: [],

    // Step 2: Connection Configuration
    connectionConfig: {
      type: 'stdio',
      command: '',
      args: [],
      env: {}
    },

    // Step 3: Capabilities & Dependencies
    mcpVersion: '1.0.0',
    programmingLanguage: '',
    license: '',
    capabilities: [],
    dependencies: {},

    // Step 4: Compatible Clients
    compatibleClients: [],

    // Step 5: Environment Variables
    environmentVars: {},

    // Step 6: Setup Instructions
    setupInstructions: '',

    // Step 7: Repository Links
    githubUrl: '',
    npmPackage: '',
    pipPackage: '',

    // Step 8: Review & Publish
    isPublished: true,
  });

  useEffect(() => {
    fetchCategories();

    // Warn user before leaving with unsaved changes
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error('Failed to load categories');
    }
  };

  const updateFormData = (updates) => {
    setFormData({ ...formData, ...updates });
    setHasUnsavedChanges(true);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.title.trim()) {
          toast.error('Title is required');
          return false;
        }
        if (formData.title.length > 255) {
          toast.error('Title must be 255 characters or less');
          return false;
        }
        if (!formData.description.trim()) {
          toast.error('Description is required');
          return false;
        }
        return true;

      case 2:
        if (!formData.connectionConfig || !formData.connectionConfig.type) {
          toast.error('Connection configuration is required');
          return false;
        }
        try {
          // Validate JSON structure
          if (typeof formData.connectionConfig !== 'object') {
            toast.error('Invalid connection configuration format');
            return false;
          }
        } catch (error) {
          toast.error('Invalid JSON in connection configuration');
          return false;
        }
        return true;

      case 3:
        if (!formData.mcpVersion) {
          toast.error('MCP version is required');
          return false;
        }
        return true;

      case 4:
        // Compatible clients are optional but recommended
        if (formData.compatibleClients.length === 0) {
          const proceed = window.confirm(
            'No compatible clients specified. Continue anyway? This may make it harder for users to discover your server.'
          );
          return proceed;
        }
        return true;

      case 5:
        // Environment variables are optional
        return true;

      case 6:
        if (!formData.setupInstructions || formData.setupInstructions.trim().length < 10) {
          toast.error('Please provide setup instructions (at least 10 characters)');
          return false;
        }
        return true;

      case 7:
        // Repository links are optional
        // Validate URLs if provided
        const urlPattern = /^https?:\/\/.+/;
        if (formData.githubUrl && !urlPattern.test(formData.githubUrl)) {
          toast.error('GitHub URL must be a valid URL starting with http:// or https://');
          return false;
        }
        return true;

      case 8:
        // Final validation before submit
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const handleStepClick = (step) => {
    // Allow jumping to previous steps, validate before jumping forward
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step > currentStep) {
      // Validate all steps between current and target
      for (let i = currentStep; i < step; i++) {
        if (!validateStep(i)) {
          return;
        }
      }
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(8)) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        iconUrl: formData.iconUrl || null,
        connectionConfig: formData.connectionConfig,
        mcpVersion: formData.mcpVersion,
        programmingLanguage: formData.programmingLanguage || null,
        license: formData.license || null,
        capabilities: formData.capabilities,
        dependencies: formData.dependencies,
        environmentVars: formData.environmentVars,
        compatibleClients: formData.compatibleClients,
        setupInstructions: formData.setupInstructions,
        categoryId: formData.categoryId || null,
        tags: formData.tags,
        githubUrl: formData.githubUrl || null,
        npmPackage: formData.npmPackage || null,
        pipPackage: formData.pipPackage || null,
        isPublished: formData.isPublished,
      };

      const response = await mcpServersAPI.create(payload);
      setHasUnsavedChanges(false);
      toast.success('MCP Server created successfully!');
      navigate(`/mcp-servers/${response.data.server.id}`);
    } catch (error) {
      console.error('Create MCP server error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create MCP server';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      categories,
    };

    switch (currentStep) {
      case 1:
        return <MCPServerFormStep1 {...stepProps} />;
      case 2:
        return <MCPServerFormStep2 {...stepProps} />;
      case 3:
        return <MCPServerFormStep3 {...stepProps} />;
      case 4:
        return <MCPServerFormStep4 {...stepProps} />;
      case 5:
        return <MCPServerFormStep5 {...stepProps} />;
      case 6:
        return <MCPServerFormStep6 {...stepProps} />;
      case 7:
        return <MCPServerFormStep7 {...stepProps} />;
      case 8:
        return <MCPServerFormStep8 {...stepProps} onEdit={setCurrentStep} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-spooky-bg-darker py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-spooky-ghost-white flex items-center gap-3">
            <span className="text-4xl">{contentTypes.mcp_server.icon}</span>
            Create MCP Server
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Share your Model Context Protocol server with the community
          </p>
        </div>

        {/* Progress Indicator */}
        <WizardProgress
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepClick={handleStepClick}
          color={mcpColor.primary}
        />

        {/* Form Step Content */}
        <Card className="mb-8">
          {renderStep()}
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-spooky-bg-medium transition text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>

          <div className="flex gap-4">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevious}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-spooky-bg-medium transition text-gray-700 dark:text-gray-300 inline-flex items-center gap-2"
              >
                <FaArrowLeft />
                Previous
              </button>
            )}

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                style={{ backgroundColor: mcpColor.primary }}
                className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition inline-flex items-center gap-2"
              >
                Next
                <FaArrowRight />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                style={{ backgroundColor: mcpColor.primary }}
                className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {loading ? (
                  'Publishing...'
                ) : (
                  <>
                    <FaRocket />
                    Publish MCP Server
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMCPServer;
